import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** A count shown after the label, e.g. how many documents are on file. */
  count?: number;
  /** Draws the count in amber, for a count that is waiting on somebody. */
  countWaiting?: boolean;
}

export interface TabsProps<T extends string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Named for screen readers, e.g. "Employee record". */
  label: string;
  className?: string;
}

/**
 * Tabs, drawn as an underline rather than a segmented pill.
 *
 * A pill switch says "pick one of these settings". An underline says "you are
 * looking at part of one thing", which is what a record's sections and a
 * queue's filters actually are. The rule runs the full width so the active
 * mark reads as sitting on a baseline rather than floating, and the row
 * scrolls rather than wraps: a record with seven sections should not push its
 * own content down a line on a phone.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: TabsProps<T>) {
  return (
    <div className={cn("u-scroll-x border-b border-line", className)}>
      <div role="tablist" aria-label={label} className="flex min-w-max gap-4">
        {items.map((item) => {
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(item.value)}
              className={cn(
                // -mb-px so the open tab's mark sits on the rule, not above it.
                "-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-0.5 pb-2 pt-1",
                "text-base font-medium transition-colors",
                selected
                  ? "border-accent text-fg"
                  : "border-transparent text-fg-subtle hover:border-line-strong hover:text-fg",
              )}
            >
              {item.label}
              {item.count !== undefined && item.count > 0 ? (
                <Count value={item.count} waiting={item.countWaiting} selected={selected} />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Count({
  value,
  waiting,
  selected,
}: {
  value: number;
  waiting?: boolean;
  selected: boolean;
}) {
  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-0.5 text-2xs font-bold tabular-nums",
        waiting
          ? "bg-ochre-100 text-ochre-700"
          : selected
            ? "bg-brand-50 text-brand-700"
            : "bg-ink-100 text-fg-subtle",
      )}
    >
      {value}
    </span>
  );
}

/** A tab's contents. Keeps the panel wiring in one place. */
export function TabPanel({
  when,
  value,
  children,
}: {
  when: string;
  value: string;
  children: ReactNode;
}) {
  if (when !== value) return null;
  return (
    <div role="tabpanel" className="space-y-5">
      {children}
    </div>
  );
}
